<!--
  EventResourceModal.vue
  이벤트 자료실 모달 - 파일 선택용

  주요 기능:
  - 파일 목록 조회
  - 폴더 탐색
  - 파일 선택 (URL emit)
-->
<template>
  <a-modal
    :open="open"
    :footer="null"
    @cancel="handleCancel"
    width="800px"
    class="resource-modal"
  >
    <template #title>
      <div class="modal-title">
        📂 파일 선택
      </div>
    </template>

    <div class="modal-content">
      <!-- 파일 업로드 섹션 -->
      <div class="upload-section">
        <div class="section-header">
          <h4>📤 파일 업로드</h4>
        </div>

        <div class="upload-form">
          <a-form layout="vertical" size="small">
            <!-- 파일 선택 -->
            <a-form-item label="파일 선택">
              <a-upload
                v-model:file-list="fileList"
                :multiple="true"
                :before-upload="beforeUpload"
                :show-upload-list="true"
                @remove="handleRemove"
              >
                <a-button size="small">
                  <template #icon><i class="fa fa-upload"></i></template>
                  파일 선택
                </a-button>
              </a-upload>
              <div class="hint-text">
                * 최대 10MB, 이미지/문서 파일만 업로드 가능
              </div>
            </a-form-item>

            <!-- 폴더 경로 (선택) -->
            <a-form-item label="폴더 경로 (선택)" v-if="fileList.length > 0">
              <a-input
                v-model:value="folderPath"
                placeholder="예) banner/main (입력하지 않으면 events/ 에 저장)"
                size="small"
                allow-clear
              />
              <div class="hint-text" v-if="folderPath">
                저장 경로: <strong>events/{{ folderPath }}/</strong>
              </div>
            </a-form-item>

            <!-- 리소스 타입 -->
            <a-form-item label="리소스 분류" v-if="fileList.length > 0">
              <a-select v-model:value="resourceType" size="small">
                <a-select-option value="image">이미지</a-select-option>
                <a-select-option value="document">문서</a-select-option>
                <a-select-option value="other">기타</a-select-option>
              </a-select>
            </a-form-item>

            <!-- 설명 (선택) -->
            <a-form-item label="설명 (선택)" v-if="fileList.length > 0">
              <a-textarea
                v-model:value="description"
                :rows="2"
                size="small"
                placeholder="파일에 대한 간단한 설명"
              />
            </a-form-item>

            <!-- 업로드 버튼 -->
            <a-form-item v-if="fileList.length > 0">
              <div style="display: flex; gap: 8px; justify-content: flex-end;">
                <a-button size="small" @click="clearUpload">취소</a-button>
                <a-button
                  size="small"
                  type="primary"
                  :loading="uploading"
                  @click="handleUpload"
                  class="yellow-button"
                >
                  <template #icon><i class="fa fa-upload"></i></template>
                  업로드
                </a-button>
              </div>
            </a-form-item>
          </a-form>
        </div>
      </div>

      <!-- 구분선 -->
      <a-divider style="margin: 16px 0;" />

      <!-- 경로 네비게이션 -->
      <div class="path-navigation">
        <a-breadcrumb>
          <a-breadcrumb-item>
            <a @click="handleBreadcrumbClick('events/')">
              <i class="fa fa-home"></i> 홈
            </a>
          </a-breadcrumb-item>
          <a-breadcrumb-item
            v-for="(crumb, index) in pathBreadcrumbs.slice(1)"
            :key="index"
          >
            <a
              v-if="!crumb.isLast"
              @click="handleBreadcrumbClick(crumb.path)"
            >
              {{ crumb.name }}
            </a>
            <span v-else>{{ crumb.name }}</span>
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>

      <!-- 필터 -->
      <div class="filter-bar">
        <a-radio-group v-model:value="currentFilter" @change="handleFilterChange" size="small">
          <a-radio-button value="all">전체</a-radio-button>
          <a-radio-button value="image">이미지</a-radio-button>
          <a-radio-button value="document">문서</a-radio-button>
          <a-radio-button value="other">기타</a-radio-button>
        </a-radio-group>
        <a-button @click="refreshFiles" :loading="loading" size="small">
          <template #icon><i class="fa fa-sync-alt"></i></template>
          새로고침
        </a-button>
      </div>

      <!-- 파일 리스트 -->
      <div class="file-list-container">
        <a-spin :spinning="loading">
          <div v-if="filteredFiles.length === 0 && !loading" class="empty-state">
            <i class="fa fa-folder-open empty-icon"></i>
            <p>파일이 없습니다.</p>
          </div>

          <div v-else class="file-items">
            <div
              v-for="item in filteredFiles"
              :key="item.path"
              class="file-item"
              :class="{
                'is-folder': item.isFolder,
                'is-clickable': item.isFolder,
                'is-selected': selectedFile?.path === item.path
              }"
              @click="handleItemClick(item)"
            >
              <div class="file-info">
                <div class="file-icon" :class="{ 'folder-icon': item.isFolder, 'image-icon': isImageFile(item) }">
                  <!-- 이미지 파일인 경우 썸네일 표시 -->
                  <img v-if="isImageFile(item) && item.url" :src="item.url" :alt="item.name" class="image-thumbnail" />
                  <!-- 아이콘 표시 -->
                  <i v-else :class="getItemIcon(item)"></i>
                </div>
                <div class="file-details">
                  <div class="file-name">
                    {{ item.name }}
                    <span v-if="item.isFolder" class="folder-badge">폴더</span>
                  </div>
                  <div class="file-meta" v-if="!item.isFolder">
                    <span class="file-size">{{ formatFileSize(item.size) }}</span>
                    <span class="file-separator">•</span>
                    <span class="file-date">{{ formatDate(item.lastModified) }}</span>
                  </div>
                  <div class="file-meta" v-else>
                    <span class="folder-hint">클릭하여 열기</span>
                  </div>
                </div>
              </div>
              <div class="file-actions" v-if="!item.isFolder">
                <a-button
                  type="primary"
                  size="small"
                  @click.stop="handleSelectFile(item)"
                  class="yellow-button"
                >
                  선택
                </a-button>
              </div>
            </div>
          </div>
        </a-spin>
      </div>

      <!-- 선택된 파일 정보 -->
      <div v-if="selectedFile && !selectedFile.isFolder" class="selected-file-info">
        <div class="info-label">선택된 파일:</div>
        <div class="info-content">
          <strong>{{ selectedFile.name }}</strong>
          <div class="file-url">{{ selectedFile.url }}</div>
        </div>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :disabled="!selectedFile || selectedFile.isFolder"
          @click="handleConfirm"
          class="yellow-button"
        >
          확인
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  name: 'EventResourceModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    eventId: {
      type: String,
      default: null
    },
    api: {
      type: Object,
      required: true
    }
  },
  emits: ['select', 'cancel'],
  setup(props, { emit }) {
    const loading = ref(false);
    const uploading = ref(false);
    const files = ref([]);
    const currentFilter = ref('all');
    const currentPath = ref('events/');
    const selectedFile = ref(null);

    // 업로드 관련 상태
    const fileList = ref([]);
    const folderPath = ref('');
    const resourceType = ref('image');
    const description = ref('');

    // 파일 확장자로 리소스 타입 판단
    const getResourceType = (fileName) => {
      if (!fileName) return 'other';
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
      if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(ext)) return 'document';
      return 'other';
    };

    // 이미지 파일 판단
    const isImageFile = (item) => {
      if (item.isFolder || !item.name) return false;
      const ext = item.name.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    };

    // 항목 아이콘 반환
    const getItemIcon = (item) => {
      if (item.isFolder) return 'fa fa-folder';
      if (!item.name) return 'fa fa-file';
      const ext = item.name.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fa fa-file-image';
      if (ext === 'pdf') return 'fa fa-file-pdf';
      if (['doc', 'docx'].includes(ext)) return 'fa fa-file-word';
      if (['xls', 'xlsx'].includes(ext)) return 'fa fa-file-excel';
      if (['txt', 'json'].includes(ext)) return 'fa fa-file-alt';
      return 'fa fa-file';
    };

    // 경로를 breadcrumb 형태로 변환
    const pathBreadcrumbs = computed(() => {
      const parts = currentPath.value.split('/').filter(Boolean);
      const breadcrumbs = [];
      let accumulatedPath = '';
      parts.forEach((part, index) => {
        accumulatedPath += part + '/';
        breadcrumbs.push({
          name: part,
          path: accumulatedPath,
          isLast: index === parts.length - 1
        });
      });
      return breadcrumbs;
    });

    // 필터링된 파일 목록
    const filteredFiles = computed(() => {
      if (currentFilter.value === 'all') return files.value;
      return files.value.filter(file => {
        if (file.isFolder) return true; // 폴더는 항상 표시
        const resourceType = getResourceType(file.name);
        return resourceType === currentFilter.value;
      });
    });

    // 파일 크기 포맷
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    // 날짜 포맷
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (error) {
        return dateString;
      }
    };

    // 파일 목록 로드
    const loadFiles = async () => {
      loading.value = true;
      try {
        const response = await props.api.getEventResources(props.eventId, 'all', currentPath.value);
        if (response.success) {
          files.value = response.data.items || [];
          console.log(`${files.value.length}개의 항목을 불러왔습니다. (경로: ${currentPath.value})`);
        } else {
          message.error(response.error || '목록을 불러오는데 실패했습니다.');
          files.value = [];
        }
      } catch (error) {
        console.error('목록 로드 오류:', error);
        message.error('목록을 불러오는데 실패했습니다.');
        files.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 항목 클릭 핸들러
    const handleItemClick = (item) => {
      if (item.isFolder) {
        // 폴더면 탐색
        currentPath.value = item.path;
        selectedFile.value = null;
        loadFiles();
      } else {
        // 파일이면 선택
        selectedFile.value = item;
      }
    };

    // 파일 선택
    const handleSelectFile = (file) => {
      selectedFile.value = file;
      handleConfirm();
    };

    // 확인 버튼
    const handleConfirm = () => {
      if (selectedFile.value && !selectedFile.value.isFolder) {
        emit('select', selectedFile.value);
        handleCancel();
      }
    };

    // Breadcrumb 클릭
    const handleBreadcrumbClick = (path) => {
      currentPath.value = path;
      selectedFile.value = null;
      loadFiles();
    };

    // 필터 변경
    const handleFilterChange = () => {
      console.log('필터 변경:', currentFilter.value);
    };

    // 새로고침
    const refreshFiles = () => {
      loadFiles();
    };

    // 취소
    const handleCancel = () => {
      selectedFile.value = null;
      emit('cancel');
    };

    // 파일 업로드 전 검증
    const beforeUpload = (file) => {
      // 파일 유형 검사
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'application/json'
      ];

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'json'];

      // 확장자와 MIME 타입 검사
      const isAllowedType = allowedTypes.includes(file.type) || allowedExts.includes(fileExt);
      if (!isAllowedType) {
        message.error(`${file.name} 파일 형식은 지원되지 않습니다.`);
        return false;
      }

      // 파일 크기 검사 (10MB로 제한)
      const isLessThan10M = file.size / 1024 / 1024 < 10;
      if (!isLessThan10M) {
        message.error(`${file.name} 파일이 10MB를 초과합니다.`);
        return false;
      }

      // 리소스 타입 자동 감지
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) {
        resourceType.value = 'image';
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExt)) {
        resourceType.value = 'document';
      } else {
        resourceType.value = 'other';
      }

      // 수동 업로드를 위해 false 반환
      return false;
    };

    // 파일 제거
    const handleRemove = (file) => {
      const index = fileList.value.indexOf(file);
      if (index > -1) {
        fileList.value.splice(index, 1);
      }
    };

    // 업로드 취소
    const clearUpload = () => {
      fileList.value = [];
      folderPath.value = '';
      resourceType.value = 'image';
      description.value = '';
    };

    // 업로드 핸들러
    const handleUpload = async () => {
      if (fileList.value.length === 0) {
        message.warning('파일을 선택해주세요.');
        return;
      }

      if (!props.api) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }

      uploading.value = true;

      try {
        // 파일 객체 추출
        const uploadFiles = fileList.value.map(item => {
          if (item.originFileObj instanceof File) {
            return item.originFileObj;
          }
          return item;
        }).filter(file => file instanceof File);

        if (uploadFiles.length === 0) {
          message.error('유효한 파일이 없습니다.');
          uploading.value = false;
          return;
        }

        // 메타데이터 구성
        const metadata = {
          resourceType: resourceType.value,
          description: description.value || ''
        };

        // 업로드 API 호출 (폴더 경로 포함)
        const response = await props.api.uploadEventResource(props.eventId, uploadFiles, metadata, folderPath.value);

        if (response.success) {
          message.success('파일이 성공적으로 업로드되었습니다.');
          clearUpload();
          // 업로드 성공 시 파일 목록 새로고침
          await loadFiles();
        } else {
          throw new Error(response.error || '업로드 실패');
        }
      } catch (error) {
        console.error('파일 업로드 실패:', error);
        message.error(`업로드 실패: ${error.message || '알 수 없는 오류'}`);
      } finally {
        uploading.value = false;
      }
    };

    // 모달 열릴 때 파일 로드 및 초기화
    watch(() => props.open, (newVal) => {
      if (newVal) {
        currentPath.value = 'events/';
        selectedFile.value = null;
        // 업로드 섹션 초기화
        clearUpload();
        loadFiles();
      }
    });

    return {
      loading,
      uploading,
      files,
      currentFilter,
      currentPath,
      selectedFile,
      pathBreadcrumbs,
      filteredFiles,
      isImageFile,
      getItemIcon,
      formatFileSize,
      formatDate,
      handleItemClick,
      handleSelectFile,
      handleConfirm,
      handleBreadcrumbClick,
      handleFilterChange,
      refreshFiles,
      handleCancel,
      // 업로드 관련
      fileList,
      folderPath,
      resourceType,
      description,
      beforeUpload,
      handleRemove,
      clearUpload,
      handleUpload
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

// 업로드 섹션
.upload-section {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h4 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
  }

  .upload-form {
    :deep(.ant-form-item) {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(.ant-form-item-label) {
      padding: 0 0 4px;

      label {
        font-size: 12px;
        font-weight: 500;
        color: #666;
      }
    }
  }
}

.hint-text {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.path-navigation {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;

  :deep(.ant-breadcrumb) {
    font-size: 13px;

    a {
      color: #666;
      transition: color 0.2s;

      &:hover {
        color: #000;
      }
    }

    .ant-breadcrumb-separator {
      color: #d9d9d9;
    }
  }
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;

  :deep(.ant-radio-group) {
    .ant-radio-button-wrapper {
      border-radius: 4px;
    }
  }
}

.file-list-container {
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  .empty-icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 16px;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #999;
  }
}

.file-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &.is-clickable:hover {
    background-color: #e6f4ff;
    border-color: #91caff;
  }

  &.is-folder {
    background-color: #fafafa;
  }

  &.is-selected {
    background-color: #e6f4ff;
    border-color: #1677ff;
  }
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
}

.file-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 6px;
  font-size: 18px;
  color: #666;
  overflow: hidden;

  &.folder-icon {
    color: #faad14;
    font-size: 20px;
  }

  &.image-icon {
    padding: 0;
    background-color: #f0f0f0;
  }

  .image-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .folder-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    background-color: #e6f4ff;
    color: #1677ff;
    font-size: 11px;
    border-radius: 4px;
    font-weight: 600;
  }
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;

  .folder-hint {
    color: #1677ff;
    font-style: italic;
  }
}

.file-size,
.file-date {
  white-space: nowrap;
}

.file-separator {
  color: #ddd;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.selected-file-info {
  padding: 12px;
  background-color: #e6f4ff;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #91caff;

  .info-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }

  .info-content {
    strong {
      font-size: 14px;
      color: #000;
    }

    .file-url {
      font-size: 11px;
      color: #666;
      margin-top: 4px;
      word-break: break-all;
      font-family: monospace;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;

  &:hover:not(:disabled) {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed;
  }
}

// 반응형
@media (max-width: 768px) {
  .modal-content {
    padding: 16px;
  }

  .filter-bar {
    flex-direction: column;
    gap: 8px;
  }

  .file-list-container {
    max-height: 300px;
  }

  .file-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .file-info {
    width: 100%;
  }

  .file-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
